// Comandos do agregado Conta (issue #16) — Contas a Pagar e a Receber, que são
// o mesmo agregado separado pela `direcao` (ADR-0011), mais os Pagamentos.
//
// Formato do ADR-0013: os `validar…` devolvem os tipos do domínio, sem tipo
// espelho e sem `as`; `direcao` e `fonte` são uniões fechadas e usam `umDe`.

import type { Snapshot } from "../domain/snapshot.ts";
import { DIRECOES_CONTA, FONTES_CONTA, type ItemConta } from "../domain/snapshot.ts";
import {
  criarConta,
  editarConta,
  excluirConta,
  registrarPagamento,
  removerPagamento,
  type DadosConta,
  type DadosPagamento,
} from "../domain/contas.ts";
import { assertNunca } from "../lib/assert-nunca.ts";
import {
  comoObjeto,
  exigirTexto,
  paraNumero,
  texto,
  umDe,
} from "./_forma.ts";
import type { ContextoComando } from "./lotes.ts";

export type ComandoConta =
  | { tipo: "criar"; id: string; dados: DadosConta }
  | { tipo: "editar"; id: string; dados: DadosConta }
  | { tipo: "excluir"; id: string }
  | { tipo: "registrarPagamento"; id: string; contaId: string; dados: DadosPagamento }
  | { tipo: "removerPagamento"; id: string };

export function executarComandoConta(
  snapshot: Snapshot,
  cmd: ComandoConta,
  _ctx: ContextoComando,
): Snapshot {
  switch (cmd.tipo) {
    case "criar":
      return criarConta(snapshot, cmd.dados, cmd.id);
    case "editar":
      return editarConta(snapshot, cmd.id, cmd.dados);
    case "excluir":
      return excluirConta(snapshot, cmd.id);
    case "registrarPagamento":
      return registrarPagamento(snapshot, cmd.contaId, cmd.dados, cmd.id);
    case "removerPagamento":
      return removerPagamento(snapshot, cmd.id);
    default:
      return assertNunca(cmd);
  }
}

function validarDados(valor: unknown): DadosConta {
  const o = comoObjeto(valor, "Dados da conta");
  const direcao = umDe(o.direcao, "direcao", DIRECOES_CONTA);
  // Conta a Pagar manda o avulso (o total é calculado no domínio, #87); a
  // Receber, o valor. `descricao`, `valor` e `vencimento` o domínio valida
  // com mensagem amigável — a fronteira passa cru e nunca inventa valor (ADR-0013)
  if (direcao === "pagar" && o.valorCentavos !== undefined) {
    // tela antiga (antes do #87) mandando um total pronto: aceitar perderia o avulso
    throw new Error("A Conta a Pagar não recebe valor pronto (avulso + itens). Recarregue a página.");
  }
  const valorDaConta =
    direcao === "pagar"
      ? { direcao, avulsoCentavos: o.avulsoCentavos == null ? null : paraNumero(o.avulsoCentavos) }
      : { direcao, valorCentavos: paraNumero(o.valorCentavos) };
  const dados: DadosConta = {
    ...valorDaConta,
    descricao: texto(o.descricao),
    vencimento: texto(o.vencimento),
    categoriaId: texto(o.categoriaId),
    fonte: o.fonte == null ? null : umDe(o.fonte, "fonte", FONTES_CONTA),
    clienteId: o.clienteId == null ? null : texto(o.clienteId),
    // A Conta de Fatura nasce só pela emissão (ADR-0011), nunca pela rota:
    // aceitar `faturaId` daqui abriria um caminho para forjar o vínculo.
    faturaId: null,
    fornecedor: o.fornecedor == null ? null : texto(o.fornecedor),
    observacao: o.observacao == null ? "" : texto(o.observacao),
  };
  if (Array.isArray(o.itens)) {
    dados.itens = o.itens.map((it: unknown) => {
      const obj = comoObjeto(it, "Item da conta");
      return {
        insumoId: texto(obj.insumoId),
        unidade: texto(obj.unidade),
        quantidade: paraNumero(obj.quantidade),
        valorUnitarioCentavos: paraNumero(obj.valorUnitarioCentavos),
      };
    });
  }
  return dados;
}

function validarPagamento(valor: unknown): DadosPagamento {
  const o = comoObjeto(valor, "Dados do pagamento");
  return {
    data: texto(o.data),
    valorCentavos: paraNumero(o.valorCentavos),
  };
}

export function validarComandoConta(valor: unknown): ComandoConta {
  const c = comoObjeto(valor, "Comando de conta");
  switch (c.tipo) {
    case "criar":
      return {
        tipo: "criar",
        id: exigirTexto(c.id, "id"),
        dados: validarDados(c.dados),
      };
    case "editar":
      return {
        tipo: "editar",
        id: exigirTexto(c.id, "id"),
        dados: validarDados(c.dados),
      };
    case "excluir":
      return { tipo: "excluir", id: exigirTexto(c.id, "id") };
    case "registrarPagamento":
      return {
        tipo: "registrarPagamento",
        id: exigirTexto(c.id, "id"),
        contaId: exigirTexto(c.contaId, "contaId"),
        dados: validarPagamento(c.dados),
      };
    case "removerPagamento":
      return { tipo: "removerPagamento", id: exigirTexto(c.id, "id") };
    default:
      throw new Error(`Tipo de comando de conta desconhecido: ${String(c.tipo)}.`);
  }
}
